<template>
    <v-container fluid fill-height>                      
        <v-layout column> 
            <v-card>                
                <v-toolbar color="primary" class="white--text">
                    <v-toolbar-title>Trap Inspections</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-icon large dark>search</v-icon>
                </v-toolbar>                
                <v-layout :row="$vuetify.breakpoint.mdAndUp" :column="$vuetify.breakpoint.smAndDown" wrap shrink>           
                    <!-- Sync Indicators -->
                    <v-flex>
                        <v-layout row wrap align-center :justify-center="$vuetify.breakpoint.smAndDown" :justify-end="$vuetify.breakpoint.mdAndUp" fill-height>
                          <v-btn round dark :color="filterVisible ? 'error' : 'primary'" @click="toggleFilter">
                              {{ filterVisible ? 'Hide Filter' : 'Show Filter' }}
                          </v-btn>  
                          <unsynced-status-indicator name="Unsynced" :unsyncedCount="unsyncedEntries"></unsynced-status-indicator>                                    
                            <v-btn icon color="error" @click="syncUnsyncedData()" v-if="unsyncedEntries > 0" :loading="syncing">
                                <v-icon>refresh</v-icon>
                            </v-btn>
                            <v-btn icon color="primary" v-else>
                                <v-icon>check</v-icon>
                            </v-btn>
                        </v-layout>
                    </v-flex>
                </v-layout>

                <div v-if="filterVisible">
                    <!-- Plant, Division and Location Selects -->
                    <ScoutingLocationSelection></ScoutingLocationSelection>

                    <v-layout row wrap shrink>
                        <v-flex xs12>
                            <v-layout  justify-center="true" row wrap align-center> 
                                <v-flex xs12 sm2 px-1 >
                                    <v-btn block color="primary" @click="clearFilter">Clear</v-btn>
                                </v-flex>
                                <v-flex xs12 sm2 px-1>
                                    <v-btn block color="primary" class="min-width-button" :disabled="!selectedLocation || traps.length === 0" @click="addInspection()">Add Inspection</v-btn>
                                </v-flex>
                            </v-layout>
                        </v-flex>
                    </v-layout>
                </div>
                
                <v-progress-linear :indeterminate="true" v-if="loading" color="primary"></v-progress-linear>
                <!-- Trap Inspections Table -->
                <v-layout v-else row wrap>
                    <v-flex xs12>
                        <v-data-table
                            :headers="headers" 
                            :items="traps"
                            :rows-per-page-items="rowsPerPageItems"
                            :pagination.sync="pagination">
                            <v-progress-linear slot="progress" color="primary" indeterminate></v-progress-linear>
                            <template slot="items" slot-scope="props">
                                <td>{{props.item.DivisionName}}</td>
                                <td>{{props.item.LocationName}}</td>
                                <td>{{props.item.TrapIdentifier}}</td>
                                <td>{{props.item.TrapType}}</td>
                                <td>{{props.item.TrapZoneName}}</td>                                    
                                <td><v-btn color="primary" block @click="$router.push({ name: 'trapinspectionhistory', params: { trapID: props.item.TrapID }})">History</v-btn></td>
                            </template>
                        </v-data-table> 
                    </v-flex>
                </v-layout>                    
            </v-card>
        </v-layout>
        <v-dialog v-model="showAddEdit" fullscreen hide-overlay transition="dialog-bottom-transition" @keydown.esc="cancelAdd">
            <v-card>            
                <trap-inspection-edit :inspection="editItem" @cancel="cancelAdd" @save="saveInspection"></trap-inspection-edit>
            </v-card>
        </v-dialog>        
    </v-container>
</template>

<script>
import UnsyncedStatusIndicator from '@/components/generic/UnsyncedStatusIndicator.vue';
import TrapInspectionEdit from '@/components/trap/TrapInspectionEdit.vue';
import ScoutingLocationSelection from "@/components/scout/ScoutingLocationSelection.vue";
import moment from 'moment';

export default {
    components: {
        UnsyncedStatusIndicator,
        TrapInspectionEdit,
        ScoutingLocationSelection
    },
    data(){
        return {
            loading: false,
            syncing: false,
            showAddEdit: false,
            editItem: null,
            pagination: {
                page: 1,
                rowsPerPage: 25,
                descending: false
            },
            rowsPerPageItems: [10, 25, 50, 100],
            headers: [
                {
                    text: "Division",
                    value: "DivisionName",
                    align: 'left'
                },
                {
                    text: "Location",
                    value: "LocationName",
                    align: 'left'
                },
                {
                    text: "Trap ID",
                    value: "TrapIdentifier",
                    align: 'left'
                },
                {
                    text: "Trap Type",
                    value: "TrapType",
                    align: 'left',
                },
                {
                    text: "Zone",
                    value: "TrapZoneName",
                    align: 'left'
                },                
                {
                    text: "Actions",
                    value: "",
                    align: "left",
                    sortable: false
                }
            ],
            filterVisible: true,
        };
    },
    watch: {
        async selectedPlant(newVal, oldVal) {
            if (newVal != oldVal && newVal) {                
                this.loading = true;

                // Try to sync anthing not synced
                await this.$store.dispatch("TrapInspectionStore/syncUnsyncedData");

                // reload both the scouting data, and the pest configuration data (wait until the previous call is complete before trying these)
                await Promise.all([
                    this.$store.dispatch("ScoutStore/loadScoutings", {plantIDs: newVal}),
                    this.$store.dispatch("ScoutStore/loadPestConfigurations", {plantIDs: newVal}),
                    this.$store.dispatch("TrapInspectionStore/loadTraps", newVal),
                    this.$store.dispatch("TrapInspectionStore/loadTrapInspectionsForPlant", { plantID: newVal })
                ]);

                this.loading = false;
            }
        }
    },
    computed: {
        unsyncedEntries() {
            return this.$store.getters["TrapInspectionStore/getUnsyncedTotal"];
        },
        selectedPlant: {
            get() {
                return this.$store.state.PestAndDiseaseStore.selectedPlant;
            },
            set(value) {
                this.$store.commit("PestAndDiseaseStore/setSelectedPlant", value);
            }
        },
        selectedDivision: {
            get() {
                return this.$store.state.PestAndDiseaseStore.selectedDivision;
            },
            set(value) {
                this.$store.commit("PestAndDiseaseStore/setSelectedDivision", value);
            }
        },
        selectedLocation: {
            get() {
                return this.$store.state.PestAndDiseaseStore.selectedLocation;
            },
            set(value) {
                this.$store.commit("PestAndDiseaseStore/setSelectedLocation", value);
            }
        },        
        traps() {
            let filteredLocationsByProductCategory = this.$store.getters["PestAndDiseaseStore/getFilteredLocations"];

            return this.$store.getters["TrapInspectionStore/getTraps"](this.selectedPlant, this.selectedDivision, this.selectedLocation, filteredLocationsByProductCategory);
        },
        plantID() {
            if (this.$route.query.plantID === undefined || this.$route.query.plantID === null) {
                return null;
            }
            return parseInt(this.$route.query.plantID);
        }
    },
    methods: {
        async loadData() {
            this.loading = true;

            if (this.plantID) {
                this.selectedPlant = this.plantID;
            }

            if (!this.selectedPlant) {
                await this.$store.dispatch("PestAndDiseaseStore/loadLocations");
            } else {
                // Lets always fetch traps but not wait for the load from the server
                this.$store.dispatch("TrapInspectionStore/loadTraps", this.selectedPlant);
                this.$store.dispatch("PestAndDiseaseStore/loadLocations");
            }

            this.loading = false;
        },
        async syncUnsyncedData() {
            this.syncing = true;

            await this.$store.dispatch("TrapInspectionStore/syncUnsyncedData");

            this.syncing = false;
        },
        async addInspection() {
            const location = this.$store.state.PestAndDiseaseStore.locations.find(l => l.LocationID == this.selectedLocation);

            this.editItem = {
                InspectionDate: moment().format('YYYY-MM-DDTHH:mm:ss'),
                LocationID: location.LocationID,
                LocationName: location.LocationName,
                Details: []
            }

            this.traps.map(t => this.editItem.Details.push({ TrapModel: t, Latitude: t.Latitude, Longitude: t.Longitude}));

            this.showAddEdit = true;
        },
        cancelAdd() {
            this.showAddEdit = false;
            this.editItem = null;
        },
        async saveInspection(item) {
            this.showAddEdit = false;
            this.loading = true;

            await this.$store.dispatch("TrapInspectionStore/saveTrapInspection", item);            

            this.loading = false;            
            this.editItem = null;
        },
        clearFilter() {
            this.selectedPlant = null;
            this.selectedDivision = null;
            this.selectedLocation = null;
        },
        toggleFilter(){
            this.filterVisible = !this.filterVisible;
        }
    },
    async created() {
        await this.loadData();
    }
}
</script>
<style scoped>
.min-width-button {
    min-width: 150px
}
</style>